import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik } from 'formik';
import { getContentKeyAccToLang } from '../../../utils/utils';
import {
  getCorpKycInvestmentPortfolio,
  getInvestmentInformationDropdowns,
  getInvestmentInformationFormData,
  saveCorpKycInvestmentPortfolio,
  saveInvestmentInformation,
} from '../../../store/actions/corporateAction';
import investment_information_validation_schema from '../../../validations_schemas/investment_information_schema';
import { Checkbox, Chips, ErrorText, RadioButton, RowView } from '../components';
import Button from '../../../components/Button';
import CustomInput from '../../../components/CustomInput';
import { Dropdown } from '../../../components';
import dynamicfunction from '../../../utils/dynamicfunction';
import { useNavigate } from 'react-router';
import { logoutUser } from '../../../store/actions/authActions';

const InvestmentInformation = ({ onDone }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { isArabic } = dynamicfunction();

  const { corporate, language } = useSelector((state) => state.content);
  const { investment_information_dropdowns, corporate_post_loading, investment_information_data } = useSelector(
    (state) => state.corporate
  );
  const {
    client_application_id,
    is_kyc,
  } = useSelector((state) => state.constant);

  const getContent = (key) => {
    return getContentKeyAccToLang(corporate?.corporate_investment_information?.data?.attributes, key, isArabic);
  };

  useEffect(() => {
    getData();
  }, [language]);

  const getData = async () => {
    await dispatch(getInvestmentInformationDropdowns());
    await dispatch(is_kyc ? getCorpKycInvestmentPortfolio(client_application_id) : getInvestmentInformationFormData(client_application_id));
  }

  const onSubmit = (values) => {
    const api_data = {
      corpClientApplicationId: client_application_id,
      hasInstitutionInvestedInFinancialSecuritiesOutsideKSA: values?.hasInstitutionInvestedInFinancialSecuritiesOutsideKSA === '1' ? true : false,
      whatIsTheInstitutionsDebtToInvestmentRatio: values?.whatIsTheInstitutionsDebtToInvestmentRatio?.id,
      whatIsTheInstitutionsGeneralInvestmentObjective: values?.whatIsTheInstitutionsGeneralInvestmentObjective,
      whatIsInstitutionsExpectedTimeHorizonForRedeemingTheseInvestments:
        values?.whatIsInstitutionsExpectedTimeHorizonForRedeemingTheseInvestments?.id,
      whaIsTheInstitutionsRiskAppetite: values?.whaIsTheInstitutionsRiskAppetite,
      fieldOfWorkId: values?.fieldOfWorkId,
      otherFieldOfWork: values?.otherFieldOfWork,
      investmentPortfolioPreferredCurrencies: values?.investmentPortfolioPreferredCurrencies,
      overallLocalInternationalInvestmentsPercentNetAssetsId: values?.overallLocalInternationalInvestmentsPercentNetAssetsId,
      investmentKnowledgeAndExperienceId: values?.investmentKnowledgeAndExperienceId,
      noOfYearsOfInvestmentInSecuritiesId: values?.noOfYearsOfInvestmentInSecuritiesId?.id,
      institutionMarginTransactionsPastFiveYearsId: values?.institutionMarginTransactionsPastFiveYearsId === '1' ? true : false,
      estimateTotalVolumeOfYourInstitutionMarginInThePastFiveYears: values?.estimateTotalVolumeOfYourInstitutionMarginInThePastFiveYears,
      extentOfInvestmentAdviceReliabilityId: values?.extentOfInvestmentAdviceReliabilityId,
      investmentActivitiesIncludedId: values?.investmentActivitiesIncludedId === '1' ? true : false,
      branchesInHighRiskCountriesId: values?.branchesInHighRiskCountriesId === '1' ? true : false,
      investmentPortfolioCountries: values?.investmentPortfolioCountries?.map((c) => ({
        portfolioCountryId: 0,
        countryId: c?.id,
      })),
      investmentPortfolioForeignCurrencies: values?.investmentPortfolioForeignCurrencies.map((c) => ({
        portfolioForeignCurrencyId: 0,
        valueId: c?.id,
      })),
      investmentPortfolioTypeOfInvestments: values?.investmentPortfolioTypeOfInvestments?.map((item) => ({
        typeOfInvestmentId: 0,
        valueId: item?.valueId,
        otherTypeOfInvestment: item?.valueId === 74 ? values?.investmentPortfolioTypeOfInvestmentsOtherSpecify : item?.otherTypeOfInvestment,
      })),
      // investmentPortfolioTypeOfInvestmentsOtherSpecify: values?.investmentPortfolioTypeOfInvestmentsOtherSpecify,
    };

    dispatch(
      is_kyc ?
        saveCorpKycInvestmentPortfolio(api_data, (res) => {
          values?.isSaveExist ? navigate('/funds/available-funds') : onDone();
        }) :
        saveInvestmentInformation(api_data, (res) => {
          values?.isSaveExist ? dispatch(logoutUser()) : onDone();
        }),
    );
  };

  const optionsYesOrNO = [
    { id: 1, name: getContent('yes') },
    { id: 0, name: getContent('no') },
  ];

  return (
    <>
      <Formik
        enableReinitialize
        initialValues={{
          fieldOfWorkId: investment_information_data?.fieldOfWorkId ?? '',
          otherFieldOfWork: investment_information_data?.otherFieldOfWork ?? '',
          overallLocalInternationalInvestmentsPercentNetAssetsId:
            investment_information_data?.overallLocalInternationalInvestmentsPercentNetAssetsId ?? '',
          investmentKnowledgeAndExperienceId: investment_information_data?.investmentKnowledgeAndExperienceId ?? '',
          whaIsTheInstitutionsRiskAppetite: investment_information_data?.whaIsTheInstitutionsRiskAppetite ?? '',
          noOfYearsOfInvestmentInSecuritiesId:
            investment_information_dropdowns?.noOfYearsOfInvestmentInSecurities?.find(
              (item) => investment_information_data?.noOfYearsOfInvestmentInSecuritiesId === item?.id
            ) ?? '',
          investmentPortfolioTypeOfInvestments: investment_information_data?.investmentPortfolioTypeOfInvestments ?? [],
          whatIsTheInstitutionsDebtToInvestmentRatio:
            investment_information_dropdowns?.loanToInvestedMoneyRatio?.find(
              (item) => item?.id === investment_information_data?.whatIsTheInstitutionsDebtToInvestmentRatio
            ) ?? '',
          institutionMarginTransactionsPastFiveYearsId:
            investment_information_data?.institutionMarginTransactionsPastFiveYearsId === true
              ? '1'
              : investment_information_data?.institutionMarginTransactionsPastFiveYearsId === false
                ? '0'
                : '',
          estimateTotalVolumeOfYourInstitutionMarginInThePastFiveYears:
            investment_information_data?.estimateTotalVolumeOfYourInstitutionMarginInThePastFiveYears ?? '',
          hasInstitutionInvestedInFinancialSecuritiesOutsideKSA:
            investment_information_data?.hasInstitutionInvestedInFinancialSecuritiesOutsideKSA === true
              ? '1'
              : investment_information_data?.hasInstitutionInvestedInFinancialSecuritiesOutsideKSA === false
                ? '0'
                : '',
          investmentPortfolioCountries:
            investment_information_dropdowns?.investmentPortfolioCountries?.filter((item) =>
              investment_information_data?.investmentPortfolioCountries?.find((c) => c?.countryId === item?.id)
            ) ?? [],
          investmentPortfolioPreferredCurrencies: investment_information_data?.investmentPortfolioPreferredCurrencies ?? [],
          whatIsTheInstitutionsGeneralInvestmentObjective: investment_information_data?.whatIsTheInstitutionsGeneralInvestmentObjective ?? [],
          preferred_investment_assets: investment_information_data?.preferred_investment_assets ?? [],
          investmentPortfolioForeignCurrencies:
            investment_information_dropdowns?.currencyDropDownList?.filter((item) =>
              investment_information_data?.investmentPortfolioForeignCurrencies?.find((c) => c?.valueId === item?.id)
            ) ?? [],
          whatIsInstitutionsExpectedTimeHorizonForRedeemingTheseInvestments:
            investment_information_dropdowns?.thePeriodDuringWhichClientExpectsCashOutInvestedMoney?.find(
              (item) => item?.id === investment_information_data?.whatIsInstitutionsExpectedTimeHorizonForRedeemingTheseInvestments
            ) ?? '',
          extentOfInvestmentAdviceReliabilityId: investment_information_data?.extentOfInvestmentAdviceReliabilityId ?? '',
          investmentActivitiesIncludedId:
            investment_information_data?.investmentActivitiesIncludedId === true
              ? '1'
              : investment_information_data?.investmentActivitiesIncludedId === false
                ? '0'
                : '',
          branchesInHighRiskCountriesId:
            investment_information_data?.branchesInHighRiskCountriesId === true
              ? '1'
              : investment_information_data?.branchesInHighRiskCountriesId === false
                ? '0'
                : '',
          investmentPortfolioTypeOfInvestmentsOtherSpecify: investment_information_data?.investmentPortfolioTypeOfInvestments?.find(
            (item) => item.valueId === 74
          )?.otherTypeOfInvestment,
          isSaveExist: false,
        }}
        validationSchema={investment_information_validation_schema}
        onSubmit={(values) => {
          onSubmit(values);
        }}
        validateOnChange={true}
        validateOnBlur={true}
      >
        {({ handleSubmit, setFieldValue, values, errors, setFieldTouched, touched }) => {
          return (
            <div className=''>
              <div>
                <Chips
                  label={getContent('field_of_work')}
                  value={values?.fieldOfWorkId}
                  onChange={(e) => {
                    setFieldValue('fieldOfWorkId', e);
                    setFieldTouched('fieldOfWorkId', false);
                  }}
                  options={investment_information_dropdowns?.fieldOfWork}
                />
                <ErrorText text={getContent(touched?.fieldOfWorkId ? errors?.fieldOfWorkId : '')} className='block' />
                {values?.fieldOfWorkId === 1519 && (
                  <div className='pt-3 sm:pt-5'>
                    <CustomInput
                      isRounded
                      value={values?.otherFieldOfWork}
                      isRequired={false}
                      onChange={(e) => {
                        setFieldValue('otherFieldOfWork', e.target.value);
                        setFieldTouched('otherFieldOfWork', true, false);
                      }}
                      placeholder={getContent('field_of_work')}
                      errorMessage={getContent(touched?.otherFieldOfWork ? errors?.otherFieldOfWork : '')}
                    />
                  </div>
                )}
              </div>

              <div className='gap-7 pt-3 sm:pt-5'>
                <Chips
                  label={getContent('investments_percentage_net_assets')}
                  value={values?.overallLocalInternationalInvestmentsPercentNetAssetsId}
                  onChange={(e) => {
                    setFieldValue('overallLocalInternationalInvestmentsPercentNetAssetsId', e);
                    setFieldTouched('overallLocalInternationalInvestmentsPercentNetAssetsId', false);
                  }}
                  options={investment_information_dropdowns?.overallLocalInternationalInvestmentsPercentNetAssets}
                />
                <ErrorText text={getContent(touched?.overallLocalInternationalInvestmentsPercentNetAssetsId ? errors?.overallLocalInternationalInvestmentsPercentNetAssetsId : '')} className='block' />
              </div>

              <div className='gap-7 pt-3 sm:pt-5'>
                <Chips
                  label={getContent('investment_knowledge_experience')}
                  value={values?.investmentKnowledgeAndExperienceId}
                  onChange={(e) => {
                    setFieldValue('investmentKnowledgeAndExperienceId', e);
                    setFieldTouched('investmentKnowledgeAndExperienceId', false);
                  }}
                  options={investment_information_dropdowns?.investmentKnowledgeAndExperience}
                />
                <ErrorText text={getContent(touched?.investmentKnowledgeAndExperienceId ? errors?.investmentKnowledgeAndExperienceId : '')} className='block' />
              </div>

              <div className='gap-7 pt-3 sm:pt-5'>
                <Chips
                  label={getContent('clients_appetite_for_risk')}
                  value={values?.whaIsTheInstitutionsRiskAppetite}
                  onChange={(e) => {
                    setFieldValue('whaIsTheInstitutionsRiskAppetite', e);
                    setFieldTouched('whaIsTheInstitutionsRiskAppetite', false);
                  }}
                  options={investment_information_dropdowns?.whaIsTheInstitutionsRiskAppetite}
                />
                <ErrorText text={getContent(touched?.whaIsTheInstitutionsRiskAppetite ? errors?.whaIsTheInstitutionsRiskAppetite : '')} className='block' />
              </div>

              <RowView className='gap-7 pt-3 sm:pt-5' isResponsive>
                <Dropdown
                  label={getContent('years_investment_securities')}
                  placeholder={getContent('placeholder_years_investment_securities')}
                  options={investment_information_dropdowns?.noOfYearsOfInvestmentInSecurities}
                  selectedValue={values?.noOfYearsOfInvestmentInSecuritiesId}
                  valueKey='name' // id
                  isRequired
                  onSelect={(val) => {
                    setFieldValue('noOfYearsOfInvestmentInSecuritiesId', val);
                    setFieldTouched('noOfYearsOfInvestmentInSecuritiesId', false);
                  }}
                  labelDivClass={"h-9 sm:h-6"}
                  errorMessage={getContent(touched?.noOfYearsOfInvestmentInSecuritiesId ? errors?.noOfYearsOfInvestmentInSecuritiesId : '')}
                />
              </RowView>

              <div className='pt-3 sm:pt-5'>
                <Checkbox
                  label={getContent('products_previously_invested_in')}
                  value={values?.investmentPortfolioTypeOfInvestments}
                  onChange={(option) => {
                    let updated_arr = [...(values?.investmentPortfolioTypeOfInvestments || [])];

                    const exists = updated_arr.some((item) => item?.valueId === option.id);

                    if (exists) {
                      updated_arr = updated_arr.filter((item) => item.valueId !== option.id);
                    } else {
                      updated_arr.push({
                        typeOfInvestmentId: 0,
                        valueId: option?.id,
                        otherTypeOfInvestment: option?.name,
                      });
                    }
                    setFieldValue('investmentPortfolioTypeOfInvestments', updated_arr);
                    setFieldTouched('investmentPortfolioTypeOfInvestments', false);
                  }}
                  options={investment_information_dropdowns?.productsPreviouslyInvestedIn}
                  isVertical
                />
                <ErrorText text={getContent(touched?.investmentPortfolioTypeOfInvestments ? errors?.investmentPortfolioTypeOfInvestments : '')} className='block' />
                {values?.investmentPortfolioTypeOfInvestments?.some((item) => item.valueId === 74) && (
                  <div className='pt-3 sm:pt-5'>
                    <CustomInput
                      isRounded
                      value={values?.investmentPortfolioTypeOfInvestmentsOtherSpecify}
                      isRequired={false}
                      onChange={(e) => {
                        setFieldValue('investmentPortfolioTypeOfInvestmentsOtherSpecify', e.target.value);
                        setFieldTouched('investmentPortfolioTypeOfInvestmentsOtherSpecify', true, false);
                      }}
                      placeholder={getContent('placeholder_margin_transactions_volume_sar_past_five_years')}
                      errorMessage={getContent(touched?.investmentPortfolioTypeOfInvestmentsOtherSpecify ? errors?.investmentPortfolioTypeOfInvestmentsOtherSpecify : '')}
                    />
                  </div>
                )}
              </div>
              <RowView className='gap-7 pt-3 sm:pt-5' isResponsive>
                <Dropdown
                  label={getContent('loan_to_invested_money_ratio')}
                  placeholder={getContent('placeholder_loan_to_invested_money_ratio')}
                  options={investment_information_dropdowns?.loanToInvestedMoneyRatio}
                  selectedValue={values?.whatIsTheInstitutionsDebtToInvestmentRatio}
                  valueKey='name'
                  isRequired
                  onSelect={(val) => {
                    setFieldValue('whatIsTheInstitutionsDebtToInvestmentRatio', val);
                    setFieldTouched('whatIsTheInstitutionsDebtToInvestmentRatio', false);
                  }}
                  errorMessage={getContent(touched?.whatIsTheInstitutionsDebtToInvestmentRatio ? errors?.whatIsTheInstitutionsDebtToInvestmentRatio : '')}
                />
              </RowView>

              <div className='pt-3 sm:pt-5'>
                <RadioButton
                  label={getContent('margin_transactions_past_five_years')}
                  value={values?.institutionMarginTransactionsPastFiveYearsId}
                  onChange={(e) => {
                    setFieldValue('institutionMarginTransactionsPastFiveYearsId', e);
                    setFieldTouched('institutionMarginTransactionsPastFiveYearsId', false);
                  }}
                  options={optionsYesOrNO}
                />
                <ErrorText text={getContent(touched?.institutionMarginTransactionsPastFiveYearsId ? errors?.institutionMarginTransactionsPastFiveYearsId : '')} className='block' />
              </div>
              {values?.institutionMarginTransactionsPastFiveYearsId?.toString() === '1' && (
                <RowView className='gap-7 pt-3 sm:pt-5' isResponsive>
                  <CustomInput
                    isRounded
                    isRequired={true}
                    label={getContent('margin_transactions_volume_sar_past_five_years')}
                    value={values?.estimateTotalVolumeOfYourInstitutionMarginInThePastFiveYears}
                    onChange={(e) => {
                      setFieldValue('estimateTotalVolumeOfYourInstitutionMarginInThePastFiveYears', e.target.value);
                      setFieldTouched('estimateTotalVolumeOfYourInstitutionMarginInThePastFiveYears', true, false);
                    }}
                    labelDivClass={"h-20 sm:h-6"}
                    placeholder={getContent('placeholder_margin_transactions_volume_sar_past_five_years')}
                    errorMessage={getContent(touched?.estimateTotalVolumeOfYourInstitutionMarginInThePastFiveYears ? errors?.estimateTotalVolumeOfYourInstitutionMarginInThePastFiveYears : '')}
                  />
                </RowView>
              )}

              <div className='pt-3 sm:pt-5'>
                <RadioButton
                  label={getContent('securities_transactions_outside_ksa_past_five_years')}
                  value={values?.hasInstitutionInvestedInFinancialSecuritiesOutsideKSA}
                  onChange={(e) => {
                    setFieldValue('hasInstitutionInvestedInFinancialSecuritiesOutsideKSA', e);
                    setFieldTouched('hasInstitutionInvestedInFinancialSecuritiesOutsideKSA', false);
                  }}
                  options={optionsYesOrNO}
                />
                <ErrorText text={getContent(touched?.hasInstitutionInvestedInFinancialSecuritiesOutsideKSA ? errors?.hasInstitutionInvestedInFinancialSecuritiesOutsideKSA : '')} className='block' />
              </div>
              {values?.hasInstitutionInvestedInFinancialSecuritiesOutsideKSA?.toString() === '1' && (
                <RowView className='gap-7 pt-3 sm:pt-5' isResponsive>
                  <Dropdown
                    label={getContent('countries_securities_transactions_outside_ksa')}
                    placeholder={getContent('placeholder_countries_securities_transactions_outside_ksa')}
                    options={investment_information_dropdowns?.investmentPortfolioCountries}
                    selectedValue={values?.investmentPortfolioCountries}
                    valueKey='name' //countryId
                    isRequired={true}
                    onSelect={(e) => {
                      setFieldValue('investmentPortfolioCountries', e);
                      setFieldTouched('investmentPortfolioCountries', false);
                    }}
                    isMulti
                    isSearch
                    labelDivClass={"h-20 sm:h-6"}
                    errorMessage={getContent(touched?.investmentPortfolioCountries ? errors?.investmentPortfolioCountries : '')}
                  />
                </RowView>
              )}

              <div className='gap-7 pt-3 sm:pt-5'>
                <Chips
                  label={getContent('general_investment_Objectives')}
                  value={values?.whatIsTheInstitutionsGeneralInvestmentObjective}
                  onChange={(e) => {
                    setFieldValue('whatIsTheInstitutionsGeneralInvestmentObjective', e);
                    setFieldTouched('whatIsTheInstitutionsGeneralInvestmentObjective', false);
                  }}
                  isMulti
                  required={false}
                  options={investment_information_dropdowns?.whatIsTheInstitutionsGeneralInvestmentObjective}
                />
                <ErrorText text={getContent(touched?.whatIsTheInstitutionsGeneralInvestmentObjective ? errors?.whatIsTheInstitutionsGeneralInvestmentObjective : '')} className='block' />
              </div>

              <div className='pt-3 sm:pt-5'>
                <Checkbox
                  label={getContent('preferred_investment_assets_heading')}
                  value={values?.investmentPortfolioPreferredCurrencies}
                  onChange={(option) => {
                    let updated_arr = [...(values?.investmentPortfolioPreferredCurrencies || [])];

                    const exists = updated_arr?.some((item) => item?.valueId === option?.id);

                    if (exists) {
                      updated_arr = updated_arr?.filter((item) => item?.valueId !== option?.id);
                    } else {
                      updated_arr.push({
                        portfolioPreferredCurrencyId: 0,
                        valueId: option?.id,
                      });
                    }
                    setFieldValue('investmentPortfolioPreferredCurrencies', updated_arr);
                    setFieldTouched('investmentPortfolioPreferredCurrencies', false);
                  }}
                  options={investment_information_dropdowns?.investmentPortfolioForeignCurrencies}
                  isVertical
                />
                <ErrorText text={getContent(touched?.investmentPortfolioPreferredCurrencies ? errors?.investmentPortfolioPreferredCurrencies : '')} className='block' />
              </div>
              {values?.investmentPortfolioPreferredCurrencies?.some((item) => item.valueId === 790) && (
                <RowView className='gap-7 pt-3 sm:pt-5' isResponsive>
                  <Dropdown
                    label={getContent('state_the_foreign_currencies')}
                    placeholder={getContent('placeholder_state_the_foreign_currencies')}
                    options={investment_information_dropdowns?.currencyDropDownList}
                    valueKey='name'
                    selectedValue={values?.investmentPortfolioForeignCurrencies}
                    isRequired
                    onSelect={(val) => {
                      setFieldValue('investmentPortfolioForeignCurrencies', val);
                      setFieldTouched('investmentPortfolioForeignCurrencies', false);
                    }}
                    isMulti
                    errorMessage={getContent(touched?.investmentPortfolioForeignCurrencies ? errors?.investmentPortfolioForeignCurrencies : '')}
                  />
                </RowView>
              )}
              <RowView className='gap-7 pt-3 sm:pt-5' isResponsive>
                <Dropdown
                  label={getContent('expected_cashout_period')}
                  placeholder={getContent('placeholder_expected_cashout_period')}
                  options={investment_information_dropdowns?.thePeriodDuringWhichClientExpectsCashOutInvestedMoney}
                  selectedValue={values?.whatIsInstitutionsExpectedTimeHorizonForRedeemingTheseInvestments}
                  valueKey='name'
                  isRequired
                  onSelect={(val) => {
                    setFieldValue('whatIsInstitutionsExpectedTimeHorizonForRedeemingTheseInvestments', val);
                    setFieldTouched('whatIsInstitutionsExpectedTimeHorizonForRedeemingTheseInvestments', false);
                  }}
                  labelDivClass={"h-14 sm:h-6"}
                  errorMessage={getContent(touched?.whatIsInstitutionsExpectedTimeHorizonForRedeemingTheseInvestments ? errors?.whatIsInstitutionsExpectedTimeHorizonForRedeemingTheseInvestments : '')}
                />
              </RowView>

              <div className='gap-7 pt-3 sm:pt-5'>
                <Chips
                  label={getContent('investment_advice_reliability')}
                  value={values?.extentOfInvestmentAdviceReliabilityId}
                  onChange={(e) => {
                    setFieldValue('extentOfInvestmentAdviceReliabilityId', e);
                    setFieldTouched('extentOfInvestmentAdviceReliabilityId', false);
                  }}
                  options={investment_information_dropdowns?.extentOfInvestmentAdviceReliability}
                />
                <ErrorText text={getContent(touched?.extentOfInvestmentAdviceReliabilityId ? errors?.extentOfInvestmentAdviceReliabilityId : '')} className='block' />
              </div>

              <div className='pt-3 sm:pt-5'>
                <RadioButton
                  label={getContent('investment_activities_included')}
                  required={false}
                  value={values?.investmentActivitiesIncludedId}
                  onChange={(e) => {
                    setFieldValue('investmentActivitiesIncludedId', e);
                    setFieldTouched('investmentActivitiesIncludedId', false);
                  }}
                  options={optionsYesOrNO}
                />
                <ErrorText text={getContent(touched?.investmentActivitiesIncludedId ? errors?.investmentActivitiesIncludedId : '')} className='block' />
              </div>

              <div className='pt-3 sm:pt-5'>
                <RadioButton
                  label={getContent('deal_or_branches_high_risk_countries')}
                  required={false}
                  value={values?.branchesInHighRiskCountriesId}
                  onChange={(e) => {
                    setFieldValue('branchesInHighRiskCountriesId', e);
                    setFieldTouched('branchesInHighRiskCountriesId', false);
                  }}
                  options={optionsYesOrNO}
                />
                <ErrorText text={getContent(touched?.branchesInHighRiskCountriesId ? errors?.branchesInHighRiskCountriesId : '')} className='block' />
              </div>

              <div className='w-full h-0.5 bg-border mt-3 sm:mt-5'></div>
              <RowView className='gap-7 pt-3 sm:pt-5'>
                <Button
                  text={getContent('save_and_proceed')}
                  color={'text-white hover:text-primary '}
                  bgColor='bg-primary hover:bg-transparent hover:border-primary'
                  className={'border border-transparent w-44 px-0 text-center'}
                  loading={corporate_post_loading}
                  isDisabled={corporate_post_loading}
                  onClick={() => {
                    setFieldValue('isSaveExist', false);
                    handleSubmit();
                  }}
                />
                <Button
                  text={getContent('save_and_exit')}
                  color={'text-primary hover:text-white'}
                  bgColor='bg-white hover:bg-primary'
                  className={'border border-primary w-44 px-0 text-center'}
                  isDisabled={corporate_post_loading}
                  onClick={() => {
                    setFieldValue('isSaveExist', true);
                    handleSubmit();
                  }}
                />
              </RowView>
            </div>
          );
        }}
      </Formik>
    </>
  );
};

export default InvestmentInformation;
